import React, { useState } from 'react';
import './AddWhiskyForm.css';
import BarcodeScanner from './BarcodeScanner';
import { whiskyLookupService } from '../services/whiskyLookup';

const AddWhiskyForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    distillery: '',
    region: '',
    age: '',
    abv: '',
    type: 'Single Malt'
  });

  const [rating, setRating] = useState({
    nose: 0,
    palate: 0,
    finish: 0,
    overall: 0,
    comments: ''
  });

  const [showRating, setShowRating] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (category, value) => {
    setRating(prev => ({
      ...prev,
      [category]: parseInt(value)
    }));
  };

  const handleCommentsChange = (e) => {
    setRating(prev => ({
      ...prev,
      comments: e.target.value
    }));
  };

  const handleBarcodeDetected = async (barcode, format) => {
    console.log('Barcode detected:', barcode, format);
    setShowScanner(false);
    setIsLookingUp(true);

    try {
      const whiskyData = await whiskyLookupService.lookupWhisky(barcode);
      
      if (whiskyData) {
        // Auto-populate form with found data
        setFormData({
          name: whiskyData.name || '',
          distillery: whiskyData.distillery || '',
          region: whiskyData.region || '',
          age: whiskyData.age || '',
          abv: whiskyData.abv || '',
          type: whiskyData.type || 'Single Malt'
        });
        
        // Show success message
        alert(`Found whisky: ${whiskyData.name}\nYou can edit the details before saving.`);
      } else {
        // No whisky found, but populate barcode for reference
        alert(`No whisky found for barcode: ${barcode}\nPlease enter the details manually.`);
        setFormData(prev => ({
          ...prev,
          name: prev.name || `Whisky ${barcode}`
        }));
      }
    } catch (error) {
      console.error('Error looking up whisky:', error);
      alert('Error looking up whisky. Please enter details manually.');
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Please enter a whisky name');
      return;
    }

    // Generate a unique ID for the new whisky
    const whiskyId = `whisky_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const newWhisky = {
      id: whiskyId,
      name: formData.name.trim(),
      distillery: formData.distillery.trim(),
      region: formData.region.trim(),
      age: formData.age.trim(),
      abv: formData.abv.trim(),
      type: formData.type
    };

    // Include rating if provided
    const whiskyRating = showRating && (rating.nose > 0 || rating.palate > 0 || rating.finish > 0 || rating.overall > 0) 
      ? rating 
      : null;

    onSubmit(newWhisky, whiskyRating);
  };

  const isRatingProvided = rating.nose > 0 || rating.palate > 0 || rating.finish > 0 || rating.overall > 0;

  if (showScanner) {
    return (
      <BarcodeScanner
        onBarcodeDetected={handleBarcodeDetected}
        onClose={() => setShowScanner(false)}
      />
    );
  }

  return (
    <div className="add-whisky-form">
      <div className="form-header">
        <h2>Add New Whisky</h2>
        <button className="close-btn" onClick={onCancel}>✕</button>
      </div>

      {isLookingUp && (
        <div className="lookup-overlay">
          <div className="lookup-spinner">
            <div className="spinner"></div>
            <p>Looking up whisky details...</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="section-header">
            <h3>Whisky Details</h3>
            <button 
              type="button" 
              className="scan-barcode-btn"
              onClick={() => setShowScanner(true)}
              title="Scan barcode to auto-fill details"
            >
              📷 Scan Barcode
            </button>
          </div>
          
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Lagavulin 16"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="distillery">Distillery</label>
            <input
              type="text"
              id="distillery"
              name="distillery"
              value={formData.distillery}
              onChange={handleInputChange}
              placeholder="e.g., Lagavulin"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="region">Region</label>
              <select
                id="region"
                name="region"
                value={formData.region}
                onChange={handleInputChange}
              >
                <option value="">Select Region</option>
                <option value="Highlands">Highlands</option>
                <option value="Lowlands">Lowlands</option>
                <option value="Speyside">Speyside</option>
                <option value="Islay">Islay</option>
                <option value="Campbeltown">Campbeltown</option>
                <option value="Islands">Islands</option>
                <option value="Ireland">Ireland</option>
                <option value="America">America</option>
                <option value="Japan">Japan</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="type">Type</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="Single Malt">Single Malt</option>
                <option value="Blended">Blended</option>
                <option value="Grain">Grain</option>
                <option value="Bourbon">Bourbon</option>
                <option value="Rye">Rye</option>
                <option value="Irish">Irish</option>
                <option value="Japanese">Japanese</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="age">Age</label>
              <input
                type="text"
                id="age"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                placeholder="e.g., 16 years, NAS"
              />
            </div>

            <div className="form-group">
              <label htmlFor="abv">ABV (%)</label>
              <input
                type="text"
                id="abv"
                name="abv"
                value={formData.abv}
                onChange={handleInputChange}
                placeholder="e.g., 43"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="rating-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={showRating}
                onChange={(e) => setShowRating(e.target.checked)}
              />
              <span>Add rating now</span>
            </label>
          </div>

          {showRating && (
            <div className="rating-section">
              <h3>Rating</h3>
              
              <div className="rating-inputs">
                {['nose', 'palate', 'finish', 'overall'].map((category) => (
                  <div key={category} className="rating-input">
                    <label>{category.charAt(0).toUpperCase() + category.slice(1)}</label>
                    <div className="rating-slider">
                      <input
                        type="range"
                        min="0"
                        max="25"
                        value={rating[category]}
                        onChange={(e) => handleRatingChange(category, e.target.value)}
                        className="slider"
                      />
                      <span className="rating-value">{rating[category]}/25</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-group">
                <label htmlFor="comments">Tasting Notes</label>
                <textarea
                  id="comments"
                  value={rating.comments}
                  onChange={handleCommentsChange}
                  placeholder="Your tasting notes and thoughts..."
                  rows="4"
                />
              </div>

              <div className="rating-total">
                <strong>Total: {rating.nose + rating.palate + rating.finish + rating.overall}/100</strong>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {showRating && isRatingProvided ? 'Add & Rate Whisky' : 'Add Whisky'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddWhiskyForm;
